/**
 * UnlockRequestControl — iter101.49
 *
 * Universal control that lives next to any "Locked" pill on the planner
 * side. Handles the full micro-flow:
 *
 *   • No pending request        → "Request Unlock" button (opens a
 *                                  dialog with a mandatory reason
 *                                  textarea, min 10 chars)
 *   • Pending request           → "Unlock requested · Awaiting admin"
 *                                  pill (with reason tooltip)
 *   • Admin approved            → "Unlocked · Re-submit to re-lock" pill
 *                                  (parent unmounts this control the
 *                                  moment the editor becomes writable)
 *   • Admin denied              → "Unlock denied · <note>" pill + a
 *                                  fresh "Request Unlock" button so
 *                                  the planner can try again
 *
 * Fetches `/api/cases/{id}/unlock-requests` on mount; the parent can
 * pass `caseObj` if it already has the case with unlock_requests + the
 * two _at flags — that avoids the extra round-trip.
 *
 * Props:
 *   caseId, section ("plan"|"mfg_spec"), caseObj (optional, for the
 *   initial state), onChanged (fires after create → parent reloads)
 */
import React, { useEffect, useState, useMemo } from "react";
import { Lock, LockOpen, Warning, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const SECTION_LABELS = {
    plan: "clinical plan",
    mfg_spec: "manufacturing spec",
};

export default function UnlockRequestControl({
    caseId,
    section,
    caseObj = null,
    onChanged,
}) {
    const [requests, setRequests] = useState(() => caseObj?.unlock_requests || []);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Refresh from the server so we always get the latest resolution state.
    const reload = async () => {
        try {
            const { data } = await api.get(`/cases/${caseId}/unlock-requests`);
            setRequests(data.requests || []);
        } catch {
            /* silent — parent can retry via onChanged */
        }
    };

    useEffect(() => {
        // Only fetch if we don't already have the requests from caseObj
        if (!caseObj?.unlock_requests) reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId]);

    // Latest request for THIS section only. `case.unlock_requests` is
    // pushed via $push so the array is oldest-first on the embedded
    // document; the dedicated GET endpoint sorts newest-first. To be
    // resilient to either shape, sort by created_at desc before picking.
    const latest = useMemo(() => {
        const filtered = (requests || []).filter((r) => r.section === section);
        filtered.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        return filtered[0] || null;
    }, [requests, section]);

    const submit = async () => {
        const trimmed = reason.trim();
        if (trimmed.length < 10) {
            toast.error("Please explain why you need the unlock (at least 10 characters).");
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/cases/${caseId}/unlock-requests`, {
                section,
                reason: trimmed,
            });
            toast.success("Unlock request sent to admin.");
            setDialogOpen(false);
            setReason("");
            await reload();
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to send unlock request");
        } finally {
            setSubmitting(false);
        }
    };

    // Pending — show the pill (button disabled).
    if (latest?.status === "pending") {
        return (
            <span
                data-testid={`unlock-request-pending-${section}`}
                title={`Reason: ${latest.reason}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 border border-amber-200 text-[10px] font-bold uppercase tracking-[0.14em] text-amber-800"
            >
                <Warning size={11} weight="fill" /> Unlock requested
            </span>
        );
    }

    // Denied — show pill + let them raise again.
    const denied = latest?.status === "denied" ? latest : null;

    return (
        <>
            <div className="inline-flex items-center gap-2 flex-wrap">
                {denied && (
                    <span
                        data-testid={`unlock-request-denied-${section}`}
                        title={`Admin note: ${denied.admin_note || "—"}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-100 border border-rose-200 text-[10px] font-bold uppercase tracking-[0.14em] text-rose-800"
                    >
                        <Warning size={11} weight="fill" /> Unlock denied
                    </span>
                )}
                <button
                    type="button"
                    onClick={() => setDialogOpen(true)}
                    data-testid={`unlock-request-open-${section}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-primary/40 text-[10px] font-bold uppercase tracking-[0.14em] text-primary hover:bg-primary/5 hover:border-primary transition"
                >
                    <LockOpen size={11} weight="bold" /> Request Unlock
                </button>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Lock size={16} weight="duotone" className="text-primary" />
                            Request unlock — {SECTION_LABELS[section] || section}
                        </DialogTitle>
                        <DialogDescription className="text-[12.5px] leading-relaxed">
                            Admin will review your reason and decide whether to unlock. Once approved, you can revise and re-submit without changing the case status.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                            Reason for unlock <span className="text-rose-600">*</span>
                        </label>
                        <Textarea
                            rows={4}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. Doctor spotted a molar attachment position issue that needs to be corrected before manufacturing starts."
                            data-testid={`unlock-request-reason-${section}`}
                            className="text-sm resize-none"
                        />
                        <p className="text-[10.5px] text-muted-foreground italic">
                            Minimum 10 characters. This message is shared with admin and stored in the case timeline.
                        </p>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setDialogOpen(false)}
                            disabled={submitting}
                            data-testid={`unlock-request-cancel-${section}`}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={submit}
                            disabled={submitting || reason.trim().length < 10}
                            data-testid={`unlock-request-submit-${section}`}
                            className="bg-primary hover:bg-primary/90"
                        >
                            <PaperPlaneTilt size={13} weight="fill" className="mr-1.5" />
                            {submitting ? "Sending…" : "Send request"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
